import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Pagination,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import { db } from "../../firebase";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";

function Paymentmode() {
  const [paymentModes, setPaymentModes] = useState([]);
  const [filteredModes, setFilteredModes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [payMode, setPayMode] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const collectionRef = collection(db, "payment_modes");

  // Fetch data from Firebase
  const fetchData = async () => {
    const snap = await getDocs(collectionRef);
    const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setPaymentModes(data);
    setFilteredModes(data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Search filter
  useEffect(() => {
    const filtered = paymentModes.filter((item) =>
      item.payMode.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredModes(filtered);
    setPage(1); // Reset page on search
  }, [search, paymentModes]);

  // Open add/edit dialog
  const handleOpenDialog = (mode = false, id = null, value = "") => {
    setEditMode(mode);
    setCurrentId(id);
    setPayMode(value);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPayMode("");
    setCurrentId(null);
  };

  // Add new payment mode
  const handleAdd = async () => {
    if (!payMode) return;
    await addDoc(collectionRef, { payMode });
    fetchData();
    handleCloseDialog();
  };

  // Update payment mode
  const handleUpdate = async () => {
    if (!payMode || !currentId) return;
    const docRef = doc(db, "payment_modes", currentId);
    await updateDoc(docRef, { payMode });
    fetchData();
    handleCloseDialog();
    fetchData();
  };

  // Delete payment mode
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this payment mode?")) {
      await deleteDoc(doc(db, "payment_modes", id));
      fetchData();
    }
  };

  // Pagination
  const paginatedData = filteredModes.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>
        Payment Modes
      </Typography>

      {/* Search + Add */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <TextField
          placeholder="Search Payment Mode"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={() => handleOpenDialog(false)}>
          Add Payment Mode
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Payment Mode</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                <TableCell>{item.payMode}</TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() =>
                      handleOpenDialog(true, item.id, item.payMode)
                    }
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No Payment Modes Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Pagination
        count={Math.ceil(filteredModes.length / rowsPerPage)}
        page={page}
        onChange={(e, value) => setPage(value)}
        color="primary"
      />

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editMode ? "Edit Payment Mode" : "Add Payment Mode"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Payment Mode"
            value={payMode}
            onChange={(e) => setPayMode(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editMode ? handleUpdate : handleAdd}
          >
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Paymentmode;